import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useMemo } from 'react';
import { useForm } from 'react-hook-form';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, PRODUCTS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

const defaultColSpan = {
	md: 12
};

const UpdateProductCategory = NiceModal.create((props) => {
	const { ids, skus } = props;
	const modal = useModal();
	const axios = useAxiosPrivate();
	const handleCloseModal = () => modal.remove();
	const { showSuccess, showError } = useAlert();
	const schema = useMemo(
		() =>
			yup.object().shape({
				category: yup.string().required()
			}),
		[]
	);

	const { LanguageCode } = useLocale();

	const CategoryAutocompleteColumns = {
		trValue: [{ accessor: 'NameTr', width: 200 }],
		engValue: [{ accessor: 'NameEn', width: 200 }],
		arabicValue: [{ accessor: 'NameAr', width: 200 }]
	};

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			category: null
		}
	});

	const {
		handleSubmit,
		watch,
		setValue,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, isLoading: isFormLoading }
	} = formMethods;
	const isLoading = isFormLoading || isSubmitting;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting);

	const onSubmit = async (data) => {
		handleCloseModal();
		try {
			const response = await axios.post(PRODUCTS.UPDATE_PRODUCT_CATEGORY, {
				productIds: Array.isArray(ids) ? ids : [ids],
				categoryId: data.category
			});
			const { status } = response;

			if (status === 200) {
				showSuccess();
				handleCloseModal();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			showError({
				message: error?.response.data.errorMessage || error?.response.data.code || error?.message
			});
		}
	};

	return (
		<Modal maxWidth="small" fullwidth className="update-product-category-modal" closeButton={true}>
			<ModalTitle>
				<Typography variant="p1">updateProductCategory</Typography>
			</ModalTitle>
			<ModalContent>
				<FormItem colSpan={defaultColSpan} label="skus" isLoading={isLoading} required>
					<StringInput value={Array.isArray(skus) ? skus.join(', ') : skus} maxLength="50" disabled />
				</FormItem>

				<Form formMethods={formMethods} borderless>
					<FormItem colSpan={defaultColSpan} label="category" isLoading={isLoading} required>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.PRODUCT_CATEGORIES}
							columns={CategoryAutocompleteColumns[LanguageCode]}
							selectionLabelAccessor={[CategoryAutocompleteColumns[LanguageCode][0].accessor]}
							value={watch('category')}
							setValue={(value) => {
								setValue('category', value?.ID ?? null, { shouldDirty: true, shouldValidate: true });
							}}
							disabled={isLoading}
							name="category"
						/>
					</FormItem>
				</Form>
			</ModalContent>

			<ModalActions>
				<Button label="cancel" variant="outline" onClick={handleCloseModal} />
				<Button
					label="save"
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
				/>
			</ModalActions>
		</Modal>
	);
});

export default UpdateProductCategory;
